// Simple's own nav, the Created AI Simple prototype's `SNAV`, in its order (spec
// ui-perf/simple-shell). An item links where its Simple page exists and is `Soon` elsewhere, so
// the PR that builds a section makes its item live with one `to` here.
import type { RegisteredRouter, RouteIds } from "@tanstack/react-router";
import { Film, Folder, House, Image, type LucideIcon, Megaphone, Tag } from "lucide-react";

/** Where an item goes: a Simple page under the brand, or none yet. */
export type SimpleTarget =
  | {
      scope: "page";
      to:
        | "/teams/$teamId/brands/$brandId/simple"
        | "/teams/$teamId/brands/$brandId/simple/videos"
        | "/teams/$teamId/brands/$brandId/simple/images"
        | "/teams/$teamId/brands/$brandId/simple/library"
        | "/teams/$teamId/brands/$brandId/simple/ads"
        | "/teams/$teamId/brands/$brandId/simple/brand";
    }
  | { scope: "soon" };

export interface SimpleNavItem {
  label: string;
  icon: LucideIcon;
  target: SimpleTarget;
}

/** The prototype's Tabler icons' Lucide twins, the ones Pro's nav draws for the same words. */
export const SIMPLE_NAV = [
  { label: "Home", icon: House, target: { scope: "page", to: "/teams/$teamId/brands/$brandId/simple" } },
  // Each section came in its own PR (spec ui-perf/simple-shell, Scope; the last, spec
  // ui-perf/simple-ads); an item with no page would be `{ scope: "soon" }`, Coming soon.
  { label: "Ads", icon: Megaphone, target: { scope: "page", to: "/teams/$teamId/brands/$brandId/simple/ads" } },
  { label: "Videos", icon: Film, target: { scope: "page", to: "/teams/$teamId/brands/$brandId/simple/videos" } },
  { label: "Images", icon: Image, target: { scope: "page", to: "/teams/$teamId/brands/$brandId/simple/images" } },
  { label: "Library", icon: Folder, target: { scope: "page", to: "/teams/$teamId/brands/$brandId/simple/library" } },
  { label: "Brand", icon: Tag, target: { scope: "page", to: "/teams/$teamId/brands/$brandId/simple/brand" } },
] as const satisfies readonly SimpleNavItem[];

export type SimpleSection = (typeof SIMPLE_NAV)[number]["label"];

const S = "/signed-in/teams/$teamId/brands/$brandId/studio/simple";
type SimplePageId = Extract<RouteIds<RegisteredRouter["routeTree"]>, typeof S | `${typeof S}/${string}`>;

/**
 * Every Simple page and the nav item it marks. A new Simple route fails tsc until it has an entry
 * here. The guided page and Home's composers are reached from Home, so they mark Home (specs
 * ui-perf/simple-home and ui-perf/simple-flows), and Images' Make an actor marks Images (spec
 * ui-perf/simple-images), and the brand's and its products' pages mark Brand (spec
 * ui-perf/simple-brand); `S` itself is the layout, never the page on screen.
 */
const PAGES: Record<SimplePageId, SimpleSection> = {
  [S]: "Home",
  [`${S}/`]: "Home",
  [`${S}/make`]: "Home",
  [`${S}/ugc-video-ad`]: "Home",
  [`${S}/image-ad`]: "Home",
  [`${S}/videos`]: "Videos",
  [`${S}/videos/$tool`]: "Videos",
  [`${S}/images`]: "Images",
  [`${S}/actor`]: "Images",
  [`${S}/fix-image`]: "Images",
  [`${S}/library`]: "Library",
  [`${S}/ads`]: "Ads",
  // A sound's page is reached from Create's picker alone: no section of its own, so Home.
  [`${S}/audio/$sound`]: "Home",
  [`${S}/brand`]: "Brand",
  [`${S}/brand/edit`]: "Brand",
  [`${S}/products/new`]: "Brand",
  [`${S}/products/$productId`]: "Brand",
};

/** The nav item of a matched route, or `undefined` off Simple (the staff pages in its frame). */
export function simpleSectionOf(routeId: string): SimpleSection | undefined {
  return (PAGES as Record<string, SimpleSection | undefined>)[routeId];
}
